/**
 * An Institution's weekly Study report as shared with a parent (SIR-VIS-05). Counts only:
 * no transcripts, answers, file names or private Study content. The Institution's name and logo
 * appear with "powered by Airbotix" (co-branding, not white-label).
 */
export interface SharedInstitutionStudyReport {
  institution: { display_name: string; logo_url: string | null };
  learner: { nickname: string };
  period: { start: string; end_exclusive: string };
  expires_at: string;
  powered_by: 'Airbotix';
  content: {
    summary?: {
      active_days?: number;
      completed_sessions?: number;
      tool_uses?: number;
      verified_checks?: number;
      verified_correct?: number;
      practice_attempts?: number;
      practice_correct?: number;
      assignments_assigned?: number;
      assignments_completed?: number;
      tutor_turns?: number;
    };
    subjects?: Array<{
      subject_key: string;
      display_name: string;
      tool_uses: number;
      practice_attempts: number;
      tutor_turns: number;
    }>;
    assignments?: Array<{
      title: string;
      status: 'assigned' | 'started' | 'completed';
      due_at: string | null;
    }>;
  };
}

const STATUS_LABEL = {
  assigned: 'Not started',
  started: 'Started',
  completed: 'Completed',
} as const;

function day(value: string) {
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
}

function count(value: number | undefined) {
  return value === undefined ? 'Unavailable' : String(value);
}

export function InstitutionStudyReportView({ report }: { report: SharedInstitutionStudyReport }) {
  const summary = report.content.summary ?? {};
  const checked = (summary.verified_checks ?? 0) + (summary.practice_attempts ?? 0);
  const correct = (summary.verified_correct ?? 0) + (summary.practice_correct ?? 0);
  const lastDay = new Date(
    new Date(report.period.end_exclusive).getTime() - 86_400_000,
  ).toISOString();
  return (
    <article className="card-base" aria-label={`${report.institution.display_name} Study report`}>
      <header className="flex flex-wrap items-center gap-4">
        {report.institution.logo_url && (
          <img
            src={report.institution.logo_url}
            alt={`${report.institution.display_name} logo`}
            className="h-12 w-auto"
          />
        )}
        <div>
          <p className="eyebrow eyebrow-mint">{report.institution.display_name}</p>
          <h2 className="mt-1 text-[24px] font-bold text-ink">
            {report.learner.nickname}&apos;s Study week
          </h2>
          <p className="text-sm text-ink-soft">
            {day(report.period.start)} to {day(lastDay)}
          </p>
        </div>
      </header>

      <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['Active days', count(summary.active_days)],
          ['Study sessions', count(summary.completed_sessions)],
          ['Tool uses', count(summary.tool_uses)],
          ['Checked answers correct', checked === 0 ? 'None checked' : `${correct} of ${checked}`],
        ].map(([label, value]) => (
          <div key={label} className="rounded-[20px] bg-surface-soft p-4">
            <dt className="text-xs font-bold uppercase tracking-wide text-slate2">{label}</dt>
            <dd className="mt-1 text-[22px] font-extrabold text-ink">{value}</dd>
          </div>
        ))}
      </dl>

      {!!report.content.subjects?.length && (
        <section className="mt-5" aria-label="Subjects">
          <h3 className="text-sm font-extrabold text-ink">Subjects</h3>
          <ul className="mt-2 grid gap-2 sm:grid-cols-2">
            {report.content.subjects.map((subject) => (
              <li
                key={subject.subject_key}
                className="rounded-[18px] border border-hairline p-3 text-sm text-ink"
              >
                <strong className="block">{subject.display_name}</strong>
                {subject.tool_uses} tool uses · {subject.practice_attempts} practice questions ·{' '}
                {subject.tutor_turns} Tutor turns
              </li>
            ))}
          </ul>
        </section>
      )}

      {!!report.content.assignments?.length && (
        <section className="mt-5" aria-label="Tasks from the Institution">
          <h3 className="text-sm font-extrabold text-ink">
            Tasks from {report.institution.display_name}
          </h3>
          <ul className="mt-2 divide-y divide-hairline text-sm text-ink">
            {report.content.assignments.map((task) => (
              <li key={`${task.title}-${task.due_at ?? ''}`} className="py-2">
                {task.title} · {STATUS_LABEL[task.status]}
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="mt-5 text-xs text-slate2">
        Counts only. Mastery levels are not assessed yet. This report does not include Tutor
        conversations, answers, uploaded files or {report.learner.nickname}&apos;s private Study.
        Link available until {day(report.expires_at)}.
      </p>
      <p className="mt-2 text-xs font-bold text-slate2">Powered by {report.powered_by}</p>
    </article>
  );
}
